import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import moment from 'moment';
import GlobalConfig from '../../../../../config';

// manifests

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';

// props
import { IPaginationResourceShort } from '../../../../../props/general';
import { FilterOptionProps } from '../../../../uiframeworks/filters/panel';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import { useStore } from '../../../../../stores/root';
import Text from './../../../../typography/text';
import SelectedFilter from '../../../../uiframeworks/filters/selected';
import Pagination from '../../../../uiframeworks/pagination';
import PermissionsService from '../../../../../services/permissions';
import GeneralService from '../../../../../services/general';
import VesselService from '../../../../../services/vessels/vessel';
import { NavLink } from 'react-router-dom';
import { IVesselRequisitionResourceShortProps } from '../../../../../props/vessels/requisitions/requisition';
import VesselRequisitionService from '../../../../../services/vessels/requisitions/requisition';
import VesselRequisitionStatus from '../../../../../manifests/status/vesselRequisition';
import Tag from '../../../../uiframeworks/tag';

type VesselRequisitionsListProps = {
    qs?: string[];
    requisitions?: IVesselRequisitionResourceShortProps[];
    columns?: TVesselRequisitionColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    hideQuickFilter?: boolean;
    hidePagination?: boolean;
    hideFilter?: boolean;
    variant?: 'card' | 'plain';
}

export type TVesselRequisitionColumn = 'name' | 'notes' | 'status' | 'vessel' | 'actions' | 'requestedAt' | 'requestedBy';

const VesselRequisitionsList: React.FC<VesselRequisitionsListProps> = (props: VesselRequisitionsListProps) => {
    const shownColumns: TVesselRequisitionColumn[] = props.columns || [
        'name',
        'notes',
        'vessel',
        'status',
        'actions',
        'requestedAt',
        'requestedBy'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [requisitions, setRequisitions] = React.useState<IVesselRequisitionResourceShortProps[]>(props.requisitions || []);
    const [activeRequisition, setActiveRequisition] = React.useState<IVesselRequisitionResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "name",
            name: "Name",
            fieldName: "name",
            minWidth: 250,
            maxWidth: 250,
            isMultiline: true,
            onRender: (item: IVesselRequisitionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <NavLink to={`/vessels/requisitions/${item.id}`}><Text size={'small'}>{item.name}</Text></NavLink>
                </Stack>
            }
        },
        {
            key: "notes",
            name: "Notes",
            fieldName: "notes",
            minWidth: 100,
            isMultiline: true,
            onRender: (item: IVesselRequisitionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.notes || "-"}</Text>
                </Stack>
            }
        },
        {
            key: "vessel",
            name: "Vessel",
            fieldName: "vessel",
            minWidth: 250,
            maxWidth: 250,
            isMultiline: true,
            onRender: (item: IVesselRequisitionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <NavLink to={`/vessels/${item.id}`}><Text size={'small'}>{item.name}</Text></NavLink>
                </Stack>
            }
        },
        {
            key: "requestedBy",
            name: "Requested By",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselRequisitionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.createdBy.name}</Text>
                </Stack>
            }
        },
        {
            key: "requestedAt",
            name: "Requested At",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselRequisitionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{moment(item.createdAt).format("DD/MM/YYYY HH:mm")}</Text>
                </Stack>
            }
        },
        {
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 100,
            isMultiline: true,
            onRender: (item: IVesselRequisitionResourceShortProps) => {
                const status = VesselRequisitionStatus.find((r) => r.key === item.status);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    <Tag size={'small'} text={status?.text} variant={status?.tagVariant} />
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.requisitions) {
            _onRetrieveRequisitions();
        } else {
            setLoaded(true);
        }
    }, [keyword, window.location.search]);

    const getQueryString = () => {
        let qs: string[] = JSON.parse(JSON.stringify(props.qs || []));
        const params = (new URLSearchParams(window.location.search));

        // get existing query string
        const _p = params.get("_p");

        qs.push(`top=${GlobalConfig.defaultTop}`);
        if ((_p || "").trim() !== "") { qs.push(`page=${_p}`) }
        if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

        qs = qs.filter(function (item, pos) {
            return qs.indexOf(item) == pos;
        });

        return qs.join("&");
    }

    const _onRetrieveRequisitions = async () => {
        try {
            setLoaded(false);
            const qs = getQueryString();
            const results = await VesselRequisitionService.retrieve(qs);

            setRequisitions(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_vessel_requisitions_list_error',
                text: 'Failed to retrieve vessel requisitions list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} styles={{ root: { marginRight: 10 } }} />
                }
            });
        }

        if (!props.hideCreateButton && PermissionsService.hasPermission(['vessels.requisitions.create'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create requisition",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <NavLink to={"/vessels/requisitions/form"}>
                        <PrimaryButton text={"Create requisition"}
                            iconProps={{ iconName: "Add" }}
                            styles={{ root: { marginLeft: 10 } }} />
                    </NavLink>
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean, status?: string) => {
        setActiveSurface(undefined);
        setActiveRequisition(undefined);

        if (refresh) { 
            _onRetrieveRequisitions();
        }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack className={styles[props.variant || 'card']} tokens={{ childrenGap: 20 }}>
                {!props.hidePagination && pagination && requisitions.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " requisitions found"} /> : null}
                {
                    !loaded || (loaded && requisitions.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={requisitions}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TVesselRequisitionColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && requisitions.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text size={'small'}>Requisitions not found</Text>
                </Stack> : null}
            </Stack>
            {!props.hidePagination && pagination && requisitions.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {/*activeSurface === 'engine.create' ? <VesselEngineForm vesselId={props.vesselId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'engine.update' && activeEngine ? <VesselEngineForm vesselId={props.vesselId} engineId={activeEngine.id} onDismissed={_onSurfaceDismissed} /> : null*/}
    </Stack>;
};

export default VesselRequisitionsList;
